import React from 'react';

// ── ICONS FOR THE PROFILE ACTION BUTTONS ─────────────────────────────────────
// The column of destinations at the right of both profile headers — the
// designer's (DesignerProfileHeader) and the customer's (CustomerProfileHeader)
// — which render the same buttons and so must draw them the same way.
//
// THESE REPLACED A ROW OF IDENTICAL ARROWS. Five buttons each ending in "→"
// gave every row the same shape, so telling them apart meant reading all five
// labels top to bottom. An arrow also said nothing the button did not: they all
// navigate, which is what a button in a list of destinations does. A distinct
// glyph per destination is scannable at a glance and says which one.
//
// LEADING, not trailing. The eye starts at the left edge of a stacked list, so
// the icon is the first thing under the cursor on every row and the labels stay
// aligned with each other.
//
// One stroke weight, one size, one viewBox for all of them, so a row of five
// reads as one set rather than five borrowed glyphs.
function Stroke({ children }) {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

const ICONS = {
  // A box, for the 3D workspace: projects are the things built in it.
  workspace: (
    <Stroke>
      <path d="M12 2.6 20.4 7v10L12 21.4 3.6 17V7z" />
      <path d="M3.6 7 12 11.6 20.4 7" />
      <path d="M12 11.6v9.8" />
    </Stroke>
  ),
  // Two people — the designers you follow, not one person.
  designers: (
    <Stroke>
      <path d="M15.6 20v-1.4a3.4 3.4 0 0 0-3.4-3.4H6.8a3.4 3.4 0 0 0-3.4 3.4V20" />
      <circle cx="9.5" cy="8.2" r="3.4" />
      <path d="M20.6 20v-1.4a3.4 3.4 0 0 0-2.6-3.3" />
      <path d="M15.4 4.9a3.4 3.4 0 0 1 0 6.6" />
    </Stroke>
  ),
  // The same heart the like button draws, at the same weight.
  liked: (
    <Stroke>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21.2l7.8-7.7 1-1.1a5.5 5.5 0 0 0 0-7.8z" />
    </Stroke>
  ),
  // A bag rather than a trolley: this is a shop's basket, not a supermarket.
  basket: (
    <Stroke>
      <path d="M5.6 7.4h12.8l-1 12.2a1.4 1.4 0 0 1-1.4 1.3H8a1.4 1.4 0 0 1-1.4-1.3z" />
      <path d="M9 7.4V5.6a3 3 0 0 1 6 0v1.8" />
    </Stroke>
  ),
  // FILLED, and the one exception to the set above. It is the sparkle the
  // credits pill and the account menu already use for this same destination —
  // one idea, one glyph — and it only reads as a sparkle when solid.
  plans: (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
      <path d="M10 3.5 11.7 8 16.2 9.7 11.7 11.4 10 15.9 8.3 11.4 3.8 9.7 8.3 8Z" />
      <path d="M17.5 14.2 18.4 16.4 20.6 17.3 18.4 18.2 17.5 20.4 16.6 18.2 14.4 17.3 16.6 16.4Z" />
    </svg>
  ),
};

/**
 * One action-button icon by name. Unknown names render nothing rather than
 * throwing — a missing glyph is a button that still works.
 */
export default function ActionIcon({ name }) {
  return ICONS[name] || null;
}
